import type { ReactElement } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.js'
import { Button } from './ui/button.js'
import type { GrantErrorKind } from '../live/useRequestCameraGrant.js'

interface Props {
  open: boolean
  kind: GrantErrorKind
  message: string
  onRetry: () => void
  onSkip: () => void
  onClose: () => void
}

const TITLE: Record<GrantErrorKind, string> = {
  'permission-denied': 'Camera access needed',
  'unsupported':       'Camera not available',
  'unknown':           "Camera didn't start",
}

const BODY: Record<GrantErrorKind, string> = {
  'permission-denied':
    "We need your camera to walk you through the scan. Open Settings → Safari → Camera and allow it for this site, then tap Retry.",
  'unsupported':
    "This browser can't open the camera. Try the latest Safari or Chrome on your phone, or skip the scan and we'll ask you a few questions instead.",
  'unknown':
    "Something blocked the camera from starting. Tap Retry to try again, or skip and we'll ask you instead.",
}

export const PermissionDeniedSheet = ({
  open, kind, message, onRetry, onSkip, onClose,
}: Props): ReactElement => {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{TITLE[kind]}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground leading-relaxed">{BODY[kind]}</p>
        {message && (
          <p className="text-xs text-muted-foreground/70 mt-2 break-words">
            Details: {message}
          </p>
        )}
        <div className="flex flex-col gap-2 mt-4">
          <Button size="xl" onClick={onRetry}>Retry</Button>
          <Button variant="ghost" onClick={onSkip}>Skip — I'll enter manually</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
